import {
  useEffect,
  useRef,
  useState,
  useCallback,
  type ClipboardEvent,
} from "react";
import { apiRequest } from "@/api/client";
import { AutoResizeTextarea, Button } from "@/components/ui";
import { Icons } from "@/components/ui/icons";
import { pickNativeFiles } from "@/desktop/pick-native-files";
import { translate, useT } from "@/i18n";
import { commonMessages } from "@/i18n/messages/common";
import { canvasFeedbackMessages } from "@/i18n/messages/canvas-feedback";
import { cn } from "@/lib/cn";
import { isTauri } from "@/lib/is-tauri";
import {
  encodeFeedbackAttachment,
  feedbackAttachmentError,
  FEEDBACK_IMAGE_EXTENSIONS,
  FEEDBACK_IMAGE_TYPES,
  MAX_FEEDBACK_ATTACHMENTS,
} from "./feedback-attachments";

export type FeedbackType = "bug" | "feature" | "other" | "ai_content";
const FEEDBACK_TYPES = [
  { value: "bug", labelKey: "typeBug" },
  { value: "feature", labelKey: "typeFeature" },
  { value: "ai_content", labelKey: "typeAiContent" },
  { value: "other", labelKey: "typeOther" },
] as const satisfies readonly { value: FeedbackType; labelKey: string }[];
interface ScreenshotAttachment {
  id: string;
  file: File;
  url: string;
}
interface FeedbackResponse {
  status: string;
  data?: { sent?: boolean };
}
interface FeedbackFormProps {
  initialType?: FeedbackType;
  onClose: () => void;
  onPendingChange?: (pending: boolean) => void;
}

function deliveryError(error: unknown): string {
  const status =
    typeof error === "object" && error !== null && "status" in error
      ? error.status
      : null;
  if (status === 429) {
    return translate(canvasFeedbackMessages, "tooManyReports");
  }
  if (status === 400 || status === 413) {
    return translate(canvasFeedbackMessages, "checkMessage");
  }
  return translate(canvasFeedbackMessages, "sendFailed");
}

export function FeedbackForm({
  initialType,
  onClose,
  onPendingChange,
}: FeedbackFormProps) {
  const t = useT(canvasFeedbackMessages);
  const tCommon = useT(commonMessages);
  const [feedbackType, setFeedbackType] = useState<FeedbackType | null>(
    initialType ?? null,
  );
  const [message, setMessage] = useState("");
  const [screenshots, setScreenshots] = useState<ScreenshotAttachment[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const screenshotsRef = useRef<ScreenshotAttachment[]>([]);
  const mountedRef = useRef(true);
  const pendingRef = useRef(false);
  const requestIdRef = useRef<string | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      screenshotsRef.current.forEach((s) => URL.revokeObjectURL(s.url));
    };
  }, []);

  const addScreenshots = useCallback((files: File[]) => {
    if (pendingRef.current || files.length === 0) return;
    const current = screenshotsRef.current;
    const attachmentError = feedbackAttachmentError([
      ...current.map((s) => s.file),
      ...files,
    ]);
    if (attachmentError) {
      setError(attachmentError);
      return;
    }
    const next = [
      ...current,
      ...files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        url: URL.createObjectURL(file),
      })),
    ];
    screenshotsRef.current = next;
    requestIdRef.current = null;
    setScreenshots(next);
    setError(null);
  }, []);

  // The desktop app's own picker shows above the Workspace overlay.
  function pickScreenshots() {
    if (!isTauri()) {
      fileInputRef.current?.click();
      return;
    }
    void pickNativeFiles({
      filter: {
        name: tCommon("images"),
        extensions: FEEDBACK_IMAGE_EXTENSIONS,
      },
      multiple: true,
    }).then((files) => {
      if (mountedRef.current) addScreenshots(files);
    });
  }

  function handlePaste(event: ClipboardEvent<HTMLDivElement>) {
    const items = Array.from(event.clipboardData.items);
    const files = items
      .filter((item) => item.type.startsWith("image/"))
      .map((item) => item.getAsFile())
      .filter((file): file is File => file !== null);
    if (files.length > 0) {
      event.preventDefault();
      addScreenshots(files);
    }
  }

  function removeScreenshot(id: string) {
    const target = screenshotsRef.current.find((s) => s.id === id);
    if (target) URL.revokeObjectURL(target.url);
    const next = screenshotsRef.current.filter((s) => s.id !== id);
    screenshotsRef.current = next;
    requestIdRef.current = null;
    setScreenshots(next);
    setError(null);
  }

  async function handleSubmit() {
    if (!feedbackType || !message.trim() || pendingRef.current || submitted) {
      return;
    }
    pendingRef.current = true;
    requestIdRef.current ??= crypto.randomUUID();
    setPending(true);
    setError(null);
    onPendingChange?.(true);
    try {
      const attachments = await Promise.all(
        screenshotsRef.current.map((s) => encodeFeedbackAttachment(s.file)),
      );
      const response = await apiRequest<FeedbackResponse>("/api/v1/feedback/", {
        method: "POST",
        body: {
          type: feedbackType,
          message: message.trim(),
          attachments,
          request_id: requestIdRef.current,
        },
        silent: true,
      });
      if (response.status !== "success" || response.data?.sent !== true) {
        throw new Error("Feedback was not accepted");
      }
      if (mountedRef.current) setSubmitted(true);
    } catch (err) {
      if (mountedRef.current) setError(deliveryError(err));
    } finally {
      pendingRef.current = false;
      if (mountedRef.current) {
        setPending(false);
        onPendingChange?.(false);
      }
    }
  }

  const isContentReport = feedbackType === "ai_content";
  if (submitted) {
    return (
      <div
        className="flex flex-col items-center gap-3 px-5 py-8"
        data-testid="feedback-success"
        role="status"
      >
        <Icons.CheckCircle className="h-5 w-5 text-accent" />
        <p className="text-appkit-body leading-5 font-medium text-primary">
          {isContentReport ? t("reportReceived") : t("thanks")}
        </p>
        <p className="text-center text-appkit-caption leading-4 text-secondary">
          {t("sentForReview")}
        </p>
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={onClose}
          data-testid="feedback-done"
        >
          {tCommon("done")}
        </Button>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col gap-3 p-4"
      data-testid="feedback-form"
      onPaste={handlePaste}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="text-appkit-caption leading-4 font-semibold text-primary">
          {isContentReport ? t("reportTitle") : t("feedbackTitle")}
        </p>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={onClose}
          disabled={pending}
          aria-label={t("closeFeedback")}
          data-testid="feedback-close"
        >
          <Icons.X className="h-3 w-3" />
        </Button>
      </div>
      <div
        className="flex flex-wrap gap-1"
        data-testid="feedback-type-selector"
      >
        {FEEDBACK_TYPES.map(({ value, labelKey }) => (
          <Button
            key={value}
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => {
              if (value !== feedbackType) requestIdRef.current = null;
              setFeedbackType(value);
            }}
            aria-pressed={feedbackType === value}
            disabled={pending}
            data-testid={`feedback-type-${value}`}
          >
            {t(labelKey)}
          </Button>
        ))}
      </div>
      <AutoResizeTextarea
        value={message}
        onChange={(event) => {
          if (event.target.value !== message) requestIdRef.current = null;
          setMessage(event.target.value);
        }}
        placeholder={
          isContentReport ? t("reportPlaceholder") : t("feedbackPlaceholder")
        }
        aria-label={isContentReport ? t("reportAria") : t("messageAria")}
        rows={4}
        maxLength={5000}
        disabled={pending}
        className={cn(
          "w-full resize-none rounded-input border border-glass-stroke-subtle bg-glass-fill px-3 py-2 text-appkit-caption leading-relaxed text-primary",
          "placeholder:text-muted focus:outline-none disabled:opacity-60",
        )}
        data-testid="feedback-message"
      />
      <p className="text-appkit-caption leading-relaxed text-secondary">
        {t("privacyNote")}
      </p>
      {screenshots.length > 0 && (
        <div
          className="scrollbar-none flex gap-2 overflow-x-auto"
          data-testid="feedback-screenshots"
        >
          {screenshots.map((s) => (
            <div key={s.id} className="relative shrink-0">
              <img
                src={s.url}
                alt={s.file.name}
                className="h-12 w-12 rounded-preview border border-glass-stroke-subtle object-cover"
              />
              <Button
                type="button"
                variant="secondary"
                size="iconXs"
                onClick={() => removeScreenshot(s.id)}
                disabled={pending}
                className="absolute right-0 top-0"
                aria-label={t("removeScreenshot", { name: s.file.name })}
                data-testid={`feedback-screenshot-remove-${s.id}`}
              >
                <Icons.X className="h-2.5 w-2.5" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {error && (
        <p
          role="alert"
          className="text-appkit-caption leading-4 text-danger-light"
          data-testid="feedback-error"
        >
          {error}
        </p>
      )}
      <input
        ref={fileInputRef}
        type="file"
        accept={FEEDBACK_IMAGE_TYPES.join(",")}
        multiple
        disabled={pending}
        className="hidden"
        onChange={(event) => {
          addScreenshots(Array.from(event.target.files ?? []));
          event.target.value = "";
        }}
        data-testid="feedback-screenshot-input"
      />
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={pickScreenshots}
          disabled={pending || screenshots.length >= MAX_FEEDBACK_ATTACHMENTS}
          title={t("addScreenshots")}
          data-testid="feedback-add-screenshot"
        >
          <Icons.Image className="h-3 w-3" />
          {t("screenshot")}
        </Button>
        <Button
          type="button"
          size="sm"
          onClick={() => void handleSubmit()}
          disabled={pending || !feedbackType || !message.trim()}
          data-testid="feedback-submit"
        >
          {pending
            ? t("sending")
            : isContentReport
              ? t("sendReport")
              : t("sendFeedback")}
        </Button>
      </div>
    </div>
  );
}
